/* global axios */
import ApiClient from './ApiClient';
import { authHeaders, compactParams, requestConfig } from './lais/httpHelpers';
import { redactLaisPayload } from './lais/redaction';

const PAYLOAD_KEYS = [
  'raw_payload',
  'rawPayload',
  'payload',
  'metadata',
  'detalhes',
];

const isObject = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const firstPresent = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

export const redactMudancaPayload = redactLaisPayload;
export const MUDANCAS_SERVICE_UNAVAILABLE_CODE = 'mudancas_service_unavailable';
const ACTIONS = {
  accept: 'accept',
  reject: 'reject',
  comment: 'comment',
};

export const normalizeMudancaItem = item => {
  if (!isObject(item)) return {};

  const rawPayload = firstPresent(
    item.raw_payload,
    item.rawPayload,
    item.payload,
    item.metadata,
    item.detalhes
  );

  const normalized = {
    ...item,
    id: firstPresent(item.id, item.uuid, item.mudanca_id),
    tipo_alteracao: firstPresent(
      item.tipo_alteracao,
      item.tipoAlteracao,
      item.tipo,
      item.change_type
    ),
    descricao_alteracao: firstPresent(
      item.descricao_alteracao,
      item.descricaoAlteracao,
      item.description,
      item.resumo
    ),
    imovel_codigo: firstPresent(
      item.imovel_codigo,
      item.imovelCodigo,
      item.property_code,
      item.listing_code
    ),
    campo_alterado: firstPresent(
      item.campo_alterado,
      item.campoAlterado,
      item.field,
      item.attribute
    ),
    valor_anterior: firstPresent(
      item.valor_anterior,
      item.valorAnterior,
      item.before,
      item.old_value
    ),
    valor_novo: firstPresent(
      item.valor_novo,
      item.valorNovo,
      item.after,
      item.new_value
    ),
    audit_status: firstPresent(
      item.audit_status,
      item.auditStatus,
      item.verification_status
    ),
    fonte_nome: firstPresent(
      item.fonte_nome,
      item.fonteNome,
      item.fonte,
      item.source_name,
      item.source,
      item.provider,
      item.origem
    ),
    fonte_tipo: firstPresent(
      item.fonte_tipo,
      item.fonteTipo,
      item.source_type,
      item.source_kind,
      item.tipo_fonte
    ),
    fonte_data: firstPresent(
      item.fonte_data,
      item.fonteData,
      item.source_observed_at,
      item.source_at,
      item.observed_at,
      item.extraido_em,
      item.extracted_at
    ),
    explicacao_regra: firstPresent(
      item.explicacao_regra,
      item.explicacaoRegra,
      item.rule_explanation,
      item.explanation,
      item.rationale,
      item.motivo_classificacao,
      item.reason
    ),
    confianca: firstPresent(
      item.confianca,
      item.confidence,
      item.confidence_score,
      item.score,
      item.trust_score
    ),
    data_solicitacao: firstPresent(
      item.data_solicitacao,
      item.dataSolicitacao,
      item.requested_at,
      item.created_at
    ),
    extraido_em: firstPresent(
      item.extraido_em,
      item.extraidoEm,
      item.extracted_at
    ),
    usuario_email: firstPresent(
      item.usuario_email,
      item.usuarioEmail,
      item.user_email,
      item.usuario?.email
    ),
    usuario_nome: firstPresent(
      item.usuario_nome,
      item.usuarioNome,
      item.user_name,
      item.usuario?.nome
    ),
  };

  PAYLOAD_KEYS.forEach(payloadKey => {
    if (normalized[payloadKey] !== undefined) {
      normalized[payloadKey] = redactMudancaPayload(normalized[payloadKey]);
    }
  });

  if (rawPayload !== undefined) {
    normalized.raw_payload = redactMudancaPayload(rawPayload);
  }

  return normalized;
};

export const normalizeMudancasList = payload => {
  const source = payload || {};
  const list = Array.isArray(source)
    ? source
    : firstPresent(source.items, source.data, source.records, []);
  const items = Array.isArray(list) ? list.map(normalizeMudancaItem) : [];
  const size = Number(
    firstPresent(source.size, source.limit, items.length, 25)
  );

  return {
    ...source,
    items,
    page: Number(firstPresent(source.page, source.current_page, 1)),
    size,
    total: Number(firstPresent(source.total, source.count, items.length)),
  };
};

export const normalizeMudancaDetail = payload => {
  if (!isObject(payload)) return {};
  if (isObject(payload.item)) return normalizeMudancaItem(payload.item);
  if (isObject(payload.data)) return normalizeMudancaItem(payload.data);
  if (
    isObject(payload.payload) &&
    firstPresent(
      payload.payload.id,
      payload.payload.uuid,
      payload.payload.tipo_alteracao,
      payload.payload.tipoAlteracao
    )
  ) {
    return normalizeMudancaItem(payload.payload);
  }
  return normalizeMudancaItem(payload);
};

export const normalizeMudancaLedgerEntry = entry => {
  if (!isObject(entry)) return {};
  return {
    ...entry,
    mudanca_id: firstPresent(entry.mudanca_id, entry.mudancaId),
    action: firstPresent(entry.action, entry.decision),
    status: firstPresent(entry.status, entry.result),
    actor_id: firstPresent(entry.actor_id, entry.actorId),
    idempotency_key_digest: firstPresent(
      entry.idempotency_key_digest,
      entry.idempotencyKeyDigest
    ),
    request_payload: redactMudancaPayload(
      firstPresent(entry.request_payload, entry.requestPayload, {})
    ),
    response_payload: redactMudancaPayload(
      firstPresent(entry.response_payload, entry.responsePayload, {})
    ),
    metadata: redactMudancaPayload(firstPresent(entry.metadata, {})),
  };
};

export const normalizeMudancaActionLedger = payload => {
  const source = payload || {};
  const list = Array.isArray(source)
    ? source
    : firstPresent(source.items, source.data, source.records, []);
  const items = Array.isArray(list)
    ? list.map(normalizeMudancaLedgerEntry)
    : [];
  return {
    ...source,
    items,
    total: Number(firstPresent(source.total, source.count, items.length)),
  };
};

export const normalizeMudancaActionResponse = payload => {
  const source = payload || {};
  const responsePayload = redactMudancaPayload(
    firstPresent(source.response, {})
  );
  return {
    ...source,
    ok: Boolean(firstPresent(source.ok, source.success, false)),
    idempotent_replay: Boolean(
      firstPresent(source.idempotent_replay, source.idempotentReplay, false)
    ),
    ledger: normalizeMudancaLedgerEntry(firstPresent(source.ledger, {})),
    response: responsePayload,
    mudanca: normalizeMudancaDetail(
      firstPresent(
        source.mudanca,
        source.item,
        source.data,
        responsePayload.item,
        responsePayload.data,
        {}
      )
    ),
  };
};

export const normalizeMudancasError = error => {
  const response = error?.response || {};
  const data = isObject(response.data) ? response.data : {};
  const status = Number(firstPresent(data.status, response.status, 0));
  const code = firstPresent(
    data.code,
    [502, 503, 504].includes(status)
      ? MUDANCAS_SERVICE_UNAVAILABLE_CODE
      : 'mudancas_request_failed'
  );
  const isUnavailable =
    code === MUDANCAS_SERVICE_UNAVAILABLE_CODE ||
    [502, 503, 504].includes(status);

  return {
    code,
    status,
    isUnavailable,
    title: isUnavailable
      ? 'Serviço de mudanças indisponível'
      : 'Não foi possível carregar mudanças',
    message: isUnavailable
      ? 'A listagem depende do serviço imobiliário externo, que não respondeu agora. Tente atualizar em alguns instantes.'
      : firstPresent(
          data.message,
          data.error,
          Array.isArray(data.errors) ? data.errors[0] : undefined,
          error?.message,
          'Não foi possível carregar mudanças.'
        ),
  };
};

const withNormalizedData = (request, normalizer) =>
  request.then(response => ({ ...response, data: normalizer(response.data) }));

const actionRequestConfig = idempotencyKey => ({
  headers: {
    ...authHeaders(),
    'Idempotency-Key': idempotencyKey,
  },
});

const generatedActionKey = (itemId, decision) =>
  [
    'dashboard',
    'mudancas',
    itemId,
    decision,
    Date.now(),
    Math.random().toString(36).slice(2, 8),
  ].join(':');

class MudancasAPI extends ApiClient {
  constructor() {
    super('mudancas', { accountScoped: true });
  }

  list(params = {}) {
    return withNormalizedData(
      axios.get(this.url, requestConfig(params)),
      normalizeMudancasList
    );
  }

  item(itemId) {
    return withNormalizedData(
      axios.get(`${this.url}/${itemId}`),
      normalizeMudancaDetail
    );
  }

  actionLedger(itemId) {
    return withNormalizedData(
      axios.get(`${this.url}/${itemId}/action_ledger`),
      normalizeMudancaActionLedger
    );
  }

  recordAction(itemId, decision, payload = {}) {
    const action = ACTIONS[decision] || decision;
    const idempotencyKey = firstPresent(
      payload.idempotency_key,
      payload.idempotencyKey,
      generatedActionKey(itemId, action)
    );
    const body = compactParams({
      decision: action,
      reason: payload.reason,
      comment: payload.comment,
      idempotency_key: idempotencyKey,
      metadata: payload.metadata,
    });

    return withNormalizedData(
      axios.post(
        `${this.url}/${itemId}/actions`,
        body,
        actionRequestConfig(idempotencyKey)
      ),
      normalizeMudancaActionResponse
    );
  }

  accept(itemId, payload = {}) {
    return this.recordAction(itemId, ACTIONS.accept, payload);
  }

  reject(itemId, payload = {}) {
    return this.recordAction(itemId, ACTIONS.reject, payload);
  }

  comment(itemId, payload = {}) {
    return this.recordAction(itemId, ACTIONS.comment, payload);
  }
}

export default new MudancasAPI();
